import { inject, Injectable } from '@angular/core';

import { ROUTES } from '@core/constants';
import {
  BrandOption,
  CreateVersionVehiclePayload,
  IResponseHttp,
  IServicesCatalogResponse,
  ModelOption,
  ServiceCategoryOption,
  VersionOption,
  YearsAndEnginesCCOption,
} from '@core/models';
import {
  ApiService,
  CatalogService,
  LookupService,
  ReceptionService,
} from '@core/services';

@Injectable({
  providedIn: 'root',
})
export class ServicesCatalogService {
  private readonly api = inject(ApiService);
  private readonly lookupService = inject(LookupService);
  private readonly catalogService = inject(CatalogService);
  private readonly receptionService = inject(ReceptionService);

  async getServices(params?: {
    page?: number;
    pageSize?: number;
    brandId?: string;
    modelId?: string;
    yearId?: string;
    engineCcId?: string;
    categoryId?: string;
    service?: string;
  }): Promise<IServicesCatalogResponse> {
    const query = new URLSearchParams();
    if (params?.page) {
      query.set('page', params.page.toString());
    }

    if (params?.pageSize) {
      query.set('pageSize', params.pageSize.toString());
    }
    if (params?.brandId) {
      query.set('brandId', params.brandId);
    }
    if (params?.modelId) {
      query.set('modelId', params.modelId);
    }
    if (params?.yearId) {
      query.set('yearId', params.yearId);
    }
    if (params?.engineCcId) {
      query.set('engineCcId', params.engineCcId);
    }
    if (params?.categoryId) {
      query.set('categoryId', params.categoryId);
    }
    if (params?.service?.trim()) {
      query.set('service', params.service.trim());
    }

    const url = `${ROUTES.ADMIN_GET_SERVICES_CATALOG}?${query.toString()}`;

    const result = await this.api.get<IResponseHttp>(url);

    return result.payload;
  }

  async getBrands(): Promise<BrandOption[]> {
    return await this.lookupService.getBrandsCars();
  }

  async getYearsAndEnginesCC(): Promise<YearsAndEnginesCCOption> {
    return await this.lookupService.getYearsAndEnginesCC();
  }

  async getModelsByBrand(brandId?: string): Promise<ModelOption[]> {
    const result = await this.lookupService.getModelsCars(brandId);
    return result;
  }

  async getVersionsByModel(modelId: string): Promise<VersionOption[]> {
    const result = await this.api.get<IResponseHttp>(
      ROUTES.LOOKUP_CARS_VERSIONS_BY_MODEL(modelId),
    );
    if (!result.payload && result.payload.length === 0) {
      return [];
    }
    return result.payload as VersionOption[];
  }

  async getServiceCategories(): Promise<ServiceCategoryOption[]> {
    const response = await this.catalogService.getServiceCategories();
    return response;
  }

  async createVersionVehicle(payload: CreateVersionVehiclePayload): Promise<string> {
    const result = this.receptionService.createVersionVehicle(payload);
    return result;
  }

  async createCatalogService(payload: {
    categoryId: string;
    name: string;
    vehicleVersionId: string;
    price: string;
  }): Promise<IResponseHttp> {
    const response = await this.catalogService.createCatalogService(payload);
    return response;
  }

  async updateCatalogService(payload: {
    id: string;
    categoryId: string;
    name: string;
    vehicleVersionId: string | null;
    price: string;
  }): Promise<IResponseHttp> {
    const response = await this.catalogService.updateCatalogService(payload);
    return response;
  }
}
